import React, {
  useState,
  useContext,
  createContext,
  useRef,
  useEffect,
} from "react";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  FileText,
  Hash,
  Type,
  List,
  Calculator,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Image,
  Link,
  Sigma,
  Check,
  X,
  Trash2,
  Settings,
  AlertTriangle,
  Download,
  Save,
  Eye,
} from "lucide-react";
import {
  ExamContext,
  PAPER_SIZES,
  createNewSection,
  createNewPage,
  createNewQuestion,
  PageSettingsModal,
  calculateRequiredPages,
  generateMetadataFromExam,
  setInitialExamData,
  generateId, // ✅ ADD this import
  updateExaminationTemplateWithMetadata, // ✅ ADD this import
  // ✅ ADD these imports
  RichContentRenderer,
  extractAllEquations,
} from "./utility.js";
import { pdf } from "@react-pdf/renderer";
import PDFDocument from "./PDFdocumnet.js"; 
import { convertMathEquationsToImages } from "./utility.js";
import {
  addTemplateSection,
  addQuestion,
  updateExaminationTemplate, // ✅ ADD this import if not present
  getExaminationTemplateDetails, // ✅ ADD this import
} from "../../../services/Users-services.js";
import { useNavigate } from "react-router-dom";
import { showErrorMessages } from "../../../services/utilityFunctions.js";
const TopToolbar = ({ setLoading, fetchExamDetails }) => {
  const context = useContext(ExamContext);
  const navigate = useNavigate();
  const [showPageSettings, setShowPageSettings] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  if (!context) return null;

  const {
    exam,
    updateExam,
    selectedNode,
    isEditingQuestion,
    clearSelection,
    setEditingMode,
  } = context;
  const currentPageSize = PAPER_SIZES[exam.pagination.paperSize];

  // Check if a section is selected for conditional button enabling
  const isSectionSelected = selectedNode?.type === "section";
  const hasAnySections = exam.pages.some((page) => page.sections.length > 0);

  // In TopToolbar.js - Replace the addSection function
  const addSection = async () => {
    setLoading(true);
    const newSection = createNewSection(exam);

    try {
      // Step 1: Add section via API
      const response = await addTemplateSection(newSection);
      if (response.status == "success") {
        // Step 2: Update local exam state with the new section
        console.log("section response:", response);
        const sectionId =
          response.data?.id || response.response?.id || generateId();
        const finalSection = {
          ...newSection,
          id: sectionId,
          questions: [], // Ensure empty questions array
        };

        const updatedExamData = {
          ...exam,
          // ✅ CRITICAL: Preserve globalEquations and richTextMetadata
          globalEquations: exam.globalEquations || {},
          richTextMetadata: exam.richTextMetadata || {},
          pages: exam.pages.map((page, index) =>
            index === 0
              ? {
                  ...page,
                  sections: [...page.sections, finalSection],
                }
              : page
          ),
        };

        // ✅ CRITICAL: Initialize richTextMetadata for the new section
        updatedExamData.richTextMetadata[sectionId] = {
          instructions: finalSection.instructions || null,
          questions: [],
        };

        // Step 3: Update metadata using utility function
        const metadataResult = await updateExaminationTemplateWithMetadata(
          updatedExamData,
          exam.id,
          fetchExamDetails
        );

        if (metadataResult.success) {
          showMessage("Section added successfully");
        } else {
          console.error(
            "Failed to update metadata after adding section:",
            metadataResult.error
          );
          // Still fetch fresh data even if metadata update failed
          await fetchExamDetails();
          showMessage("Section added successfully");
        }
      } else {
        showErrorMessages({
          data: response,
          navigate,
        });
      }
    } catch (error) {
      console.error("Error adding section:", error);
      showMessage("Failed to add section");
    }
    setLoading(false);
  };
  // ✅ FIXED: Updated addQuestionToSection function in TopToolbar.js
  const addQuestionToSection = async () => {
    // Only proceed if we have sections available
    if (!hasAnySections && !isSectionSelected) {
      showMessage("Please add a section first before adding questions");
      return;
    }
    setLoading(true);

    try {
      console.log("Adding question to section:", selectedNode);

      // ✅ FIXED: Create question with proper structure
      const newQuestion = createNewQuestion(exam, selectedNode);

      // Step 1: Add question via API
      const response = await addQuestion(newQuestion);

      if (response.status == "success") {
        // Step 2: Update local exam state with the new question
        const questionId =
          response.data?.id || response.response?.id || generateId();
        const finalQuestion = { ...newQuestion, id: questionId };

        const updatedExamData = {
          ...exam,
          // ✅ CRITICAL: Preserve globalEquations and richTextMetadata
          globalEquations: exam.globalEquations || {},
          richTextMetadata: exam.richTextMetadata || {},
          pages: exam.pages.map((page) => ({
            ...page,
            sections: page.sections.map((section) =>
              section.id === newQuestion.sectionId
                ? {
                    ...section,
                    questions: [...section.questions, finalQuestion],
                  }
                : section
            ),
          })),
        };

        // Step 3: Calculate new total marks
        const newTotalMarks = updatedExamData.pages.reduce(
          (total, page) =>
            total +
            page.sections.reduce(
              (sectionTotal, section) =>
                sectionTotal +
                section.questions.reduce(
                  (questionTotal, question) =>
                    questionTotal + (question.marks || 1),
                  0
                ),
              0
            ),
          0
        );

        // Step 4: Update exam metadata with new total marks
        updatedExamData.metadata = {
          ...updatedExamData.metadata,
          totalMarks: newTotalMarks,
        };
        updatedExamData.score = newTotalMarks;

        // ✅ CRITICAL: Update richTextMetadata for the new question
        if (!updatedExamData.richTextMetadata[newQuestion.sectionId]) {
          updatedExamData.richTextMetadata[newQuestion.sectionId] = {
            instructions: null,
            questions: [],
          };
        }

        // ✅ FIXED: Add the new question to richTextMetadata with proper structure including options
        const questionMetadata = {
          questionId: questionId,
          configuration: finalQuestion.richContent,
          // ✅ FIXED: Include options metadata for OBJECTIVE questions
          options: finalQuestion.options.map((option) => ({
            id: option.id,
            content: option.content,
            richContent: option.richContent,
          })),
          type: finalQuestion.type,
          marks: finalQuestion.marks,
          correctAnswers: finalQuestion.correctAnswers,
          rightOptions: finalQuestion.rightOptions,
          isMultiSelect: finalQuestion.isMultiSelect,
          optionsLayout: finalQuestion.optionsLayout,
        };

        updatedExamData.richTextMetadata[newQuestion.sectionId].questions.push(
          questionMetadata
        );

        // Step 5: Update metadata using utility function
        const metadataResult = await updateExaminationTemplateWithMetadata(
          updatedExamData,
          exam.id,
          fetchExamDetails
        );

        if (metadataResult.success) {
          showMessage("Question added successfully");
        } else {
          console.error(
            "Failed to update metadata after adding question:",
            metadataResult.error
          );
          // Still fetch fresh data even if metadata update failed
          await fetchExamDetails();
          showMessage("Question added successfully");
        }
      } else {
        showErrorMessages({
          data: response,
          navigate,
        });
      }
    } catch (error) {
      console.error("Error adding question:", error);
      showMessage("Failed to add question");
    }
    setLoading(false);
  };

  const showMessage = (message) => {
    setShowSuccessMessage(message);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  // ✅ UPDATED: Use the same pagination logic as Canvas.js
  const organizeContentIntoPages = () => {
    const pageHeight = currentPageSize.height - 160;
    const questionBaseHeight = 75;
    const optionHeight = 25;
    const sectionHeaderHeight = 50;
    const examHeaderHeight = 200;
    const pageHeaderHeight = 20;
    const pagePadding = 30;
    const sectionInstructionsHeight = 35;
    const sectionMargin = 15;

    let organizedPages = [];
    let currentPageHeight = 0;
    let currentPage = null;
    let pageIndex = 0;

    // Helper function to create a new page
    const createNewPageHelper = () => {
      const newPage = {
        id: `page-${Date.now()}-${pageIndex}`,
        padding: 20,
        borderStyle: "solid",
        pageBreakBefore: pageIndex > 0,
        sections: [],
      };

      // Set initial height based on page type
      if (pageIndex === 0) {
        currentPageHeight = examHeaderHeight + pagePadding; // First page with exam header
      } else {
        currentPageHeight = pageHeaderHeight + pagePadding; // Subsequent pages
      }

      pageIndex++;
      return newPage;
    };

    // Helper function to calculate question height
    const calculateQuestionHeight = (question) => {
      let height = questionBaseHeight;

      if (
        question.type === "with-options" &&
        question.options &&
        question.options.length > 0
      ) {
        height += question.options.length * optionHeight;
      }

      if (question.richContent && question.richContent.html) {
        const htmlLength = question.richContent.html.length;
        if (htmlLength > 200) {
          height += Math.floor(htmlLength / 100) * 15;
        }
      }

      return height;
    };

    // Helper function to check if content fits on current page
    const canFitOnCurrentPage = (additionalHeight) => {
      return currentPageHeight + additionalHeight <= pageHeight;
    };

    // Get all sections from exam
    const allSections = exam.pages.flatMap((page) => page.sections);

    if (allSections.length === 0) {
      // If no sections, create a single page with just the header
      const emptyPage = createNewPageHelper();
      organizedPages.push(emptyPage);
      return organizedPages;
    }

    // Start with first page
    currentPage = createNewPageHelper();

    allSections.forEach((section, sectionIndex) => {
      // ✅ CRITICAL FIX: Skip completely empty sections
      if (!section.questions || section.questions.length === 0) {
        return;
      }

      let sectionHeight = sectionHeaderHeight;
      let sectionQuestionsForCurrentPage = [];
      let remainingQuestions = [...section.questions];

      // Add height for section instructions if they exist
      if (
        section.hasInstructions &&
        section.instructions &&
        section.instructions.plainText &&
        section.instructions.plainText.trim() !== "" &&
        section.instructions.plainText !== "Enter section instructions here..."
      ) {
        sectionHeight += sectionInstructionsHeight;
      }

      // Process each question in the section
      while (remainingQuestions.length > 0) {
        const question = remainingQuestions[0];
        const questionHeight = calculateQuestionHeight(question);

        // Calculate total height needed for this question
        let totalHeightNeeded = questionHeight;

        // If this is the first question in a new section on this page, include section header
        if (sectionQuestionsForCurrentPage.length === 0) {
          totalHeightNeeded += sectionHeight + sectionMargin;
        }

        // Check if question fits on current page
        if (canFitOnCurrentPage(totalHeightNeeded)) {
          // Question fits, add it to current page
          sectionQuestionsForCurrentPage.push(remainingQuestions.shift());
          currentPageHeight +=
            sectionQuestionsForCurrentPage.length === 1
              ? totalHeightNeeded
              : questionHeight;
        } else {
          // Question doesn't fit on current page

          // If we have questions accumulated for this section on current page, add them
          if (sectionQuestionsForCurrentPage.length > 0) {
            currentPage.sections.push({
              ...section,
              questions: sectionQuestionsForCurrentPage,
              continuesOnNextPage: remainingQuestions.length > 0,
            });
            sectionQuestionsForCurrentPage = [];
          }

          // Start a new page
          organizedPages.push(currentPage);
          currentPage = createNewPageHelper();

          // Now try to add the question to the new page
          totalHeightNeeded = questionHeight + sectionHeight + sectionMargin;

          if (canFitOnCurrentPage(totalHeightNeeded)) {
            // Question fits on new page
            sectionQuestionsForCurrentPage.push(remainingQuestions.shift());
            currentPageHeight += totalHeightNeeded;
          } else {
            // Question is too large for any page - force add it anyway
            console.warn(`Question is too large for page: ${question.id}`);
            sectionQuestionsForCurrentPage.push(remainingQuestions.shift());
            currentPageHeight += totalHeightNeeded;
          }
        }
      }

      // Add any remaining questions from this section to current page
      if (sectionQuestionsForCurrentPage.length > 0) {
        currentPage.sections.push({
          ...section,
          questions: sectionQuestionsForCurrentPage,
        });
      }

      // Add small margin after each section
      currentPageHeight += sectionMargin / 2;
    });

    // ✅ CRITICAL FIX: Only add the final page if it has content OR if no pages were created at all
    if (
      currentPage &&
      (currentPage.sections.length > 0 || organizedPages.length === 0)
    ) {
      organizedPages.push(currentPage);
    }

    // ✅ CRITICAL FIX: Ensure at least one page exists only if we truly have no content
    if (organizedPages.length === 0) {
      const emptyPage = createNewPageHelper();
      organizedPages.push(emptyPage);
    }

    return organizedPages;
  };

  // Replace the createPDFContent function in TopToolbar.js with this fixed version:

  // Update the calculateTotalPages function to use the same logic:
  const loadHtml2PdfEnhanced = async () => {
    return new Promise((resolve, reject) => {
      if (window.html2pdf) {
        resolve(window.html2pdf);
        return;
      }

      const cdnUrls = [
        "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.2/html2pdf.bundle.min.js",
        "https://unpkg.com/html2pdf.js@0.10.2/dist/html2pdf.bundle.min.js",
        "https://cdn.jsdelivr.net/npm/html2pdf.js@0.10.2/dist/html2pdf.bundle.min.js",
      ];

      let currentCdnIndex = 0;

      const tryLoadFromCdn = () => {
        if (currentCdnIndex >= cdnUrls.length) {
          reject(new Error("Failed to load html2pdf from all CDNs"));
          return;
        }

        const script = document.createElement("script");
        script.src = cdnUrls[currentCdnIndex];

        script.onload = () => {
          // Wait longer for full initialization
          setTimeout(() => {
            if (window.html2pdf) {
              resolve(window.html2pdf);
            } else {
              currentCdnIndex++;
              tryLoadFromCdn();
            }
          }, 1000);
        };

        script.onerror = () => {
          currentCdnIndex++;
          tryLoadFromCdn();
        };

        document.head.appendChild(script);
      };

      tryLoadFromCdn();
    });
  };
  const waitForMathFieldsToRender = async (container) => {
    const mathFields = container.querySelectorAll("math-field");

    // Wait for each math field to be ready
    const promises = Array.from(mathFields).map((field) => {
      return new Promise((resolve) => {
        if (field.readyState === "complete" || field.value) {
          resolve();
        } else {
          const checkReady = () => {
            if (field.readyState === "complete" || field.value) {
              resolve();
            } else {
              setTimeout(checkReady, 100);
            }
          };
          checkReady();
        }
      });
    });

    await Promise.all(promises);

    // Additional wait for rendering
  };
  const createHighQualityPDFContent = async () => {
    const pdfContainer = document.createElement("div");
    pdfContainer.className = "pdf-container-hq";
    pdfContainer.style.cssText = `
      width: 100%;
      max-width: none;
      background: white;
      color: black;
      font-family: 'Times New Roman', serif;
      font-size: 12pt;
      line-height: 1.4;
      margin: 0;
      padding: 0;
      box-shadow: none;
      border: none;
      border-radius: 0;
    `;

    // Get properly organized content
    const organizedContent = getEnhancedContentForPDF();

    for (let i = 0; i < organizedContent.pages.length; i++) {
      const page = organizedContent.pages[i];
      const pageDiv = await createEnhancedPDFPage(
        page,
        i,
        organizedContent.totalPages,
        organizedContent
      );
      pdfContainer.appendChild(pageDiv);
    }

    return pdfContainer;
  };

  const calculateTotalPages = () => {
    const organizedPages = organizeContentIntoPages();
    return organizedPages.length;
  };
  const getEnhancedContentForPDF = () => {
    const allSections = exam.pages.flatMap((page) => page.sections);
    const totalMarks = calculateTotalMarks();

    // More accurate height calculations
    const HEIGHTS = {
      examHeader: 120, // Space for header
      pageHeader: 20,
      sectionHeader: 35,
      questionBase: 50,
      optionHeight: 25,
      marksLine: 15,
      sectionGap: 20,
      questionGap: 12,
      mathEquationExtra: 10, // Extra space for math
    };

    const pageHeight = currentPageSize.height - 80; // Account for margins
    let pages = [];
    let currentPage = null;
    let currentHeight = 0;
    let globalQuestionIndex = 0;

    const startNewPage = (pageIndex) => {
      currentPage = {
        pageNumber: pageIndex + 1,
        sections: [],
        isFirstPage: pageIndex === 0,
      };
      currentHeight = currentPage.isFirstPage
        ? HEIGHTS.examHeader + HEIGHTS.pageHeader
        : HEIGHTS.pageHeader;
      pages.push(currentPage);
    };

    const calculateQuestionHeight = (question) => {
      let height = HEIGHTS.questionBase;

      // Account for options
      if (question.type === "with-options" && question.options?.length > 0) {
        height += question.options.length * HEIGHTS.optionHeight;
      }

      // Account for content length
      const contentLength = question.richContent?.plainText?.length || 0;
      if (contentLength > 100) {
        height += Math.ceil(contentLength / 120) * 20;
      }

      // Extra space for math equations
      if (
        question.richContent?.equations &&
        Object.keys(question.richContent.equations).length > 0
      ) {
        height +=
          HEIGHTS.mathEquationExtra *
          Object.keys(question.richContent.equations).length;
      }

      height += HEIGHTS.marksLine;
      return height;
    };

    // Start first page
    startNewPage(0);

    allSections.forEach((section) => {
      if (!section.questions?.length) return;

      const sectionHeaderHeight = HEIGHTS.sectionHeader;
      let sectionQuestions = [...section.questions];

      // Check if section header fits
      if (
        currentHeight + sectionHeaderHeight + HEIGHTS.sectionGap >
        pageHeight
      ) {
        startNewPage(pages.length);
      }

      // Add section
      const pageSection = {
        ...section,
        questions: [],
        isPartial: false,
        startQuestionIndex: globalQuestionIndex,
      };

      currentPage.sections.push(pageSection);
      currentHeight += sectionHeaderHeight;

      // Add questions
      while (sectionQuestions.length > 0) {
        const question = sectionQuestions[0];
        const questionHeight = calculateQuestionHeight(question);

        if (
          currentHeight + questionHeight + HEIGHTS.questionGap <=
          pageHeight
        ) {
          // Question fits
          pageSection.questions.push({
            ...sectionQuestions.shift(),
            globalIndex: globalQuestionIndex++,
          });
          currentHeight += questionHeight + HEIGHTS.questionGap;
        } else {
          // Start new page
          if (pageSection.questions.length > 0) {
            pageSection.isPartial = sectionQuestions.length > 0;
          }

          startNewPage(pages.length);

          const newPageSection = {
            ...section,
            questions: [],
            isPartial: false,
            isContinuation: true,
            startQuestionIndex: globalQuestionIndex,
          };
          currentPage.sections.push(newPageSection);
          currentHeight += sectionHeaderHeight;

          // Add question to new page
          newPageSection.questions.push({
            ...sectionQuestions.shift(),
            globalIndex: globalQuestionIndex++,
          });
          currentHeight += questionHeight + HEIGHTS.questionGap;

          pageSection = newPageSection;
        }
      }

      currentHeight += HEIGHTS.sectionGap;
    });

    return {
      pages,
      totalPages: pages.length,
      metadata: exam.metadata,
      totalMarks,
    };
  };
  const createEnhancedPDFPage = async (
    page,
    pageIndex,
    totalPages,
    organizedContent
  ) => {
    const pageDiv = document.createElement("div");
    pageDiv.className = "pdf-page-enhanced";

    if (pageIndex < totalPages - 1) {
      pageDiv.classList.add("pdf-page-break");
    }

    pageDiv.style.cssText = `
      width: 100%;
      min-height: ${currentPageSize.height - 40}px;
      padding: 10mm 8mm;
      margin: 0;
      page-break-after: ${pageIndex < totalPages - 1 ? "always" : "auto"};
      page-break-inside: avoid;
      background: white;
      box-sizing: border-box;
      font-family: 'Times New Roman', serif;
      position: relative;
      overflow: hidden;
    `;

    // Add exam header on first page
    if (page.isFirstPage) {
      pageDiv.appendChild(
        createEnhancedExamHeader(organizedContent.totalMarks)
      );
    }

    // Add page number
    pageDiv.appendChild(createEnhancedPageHeader(page.pageNumber, totalPages));

    // Add sections
    for (let i = 0; i < page.sections.length; i++) {
      const section = page.sections[i];
      const sectionDiv = await createEnhancedSectionContent(section, i);
      pageDiv.appendChild(sectionDiv);
    }

    return pageDiv;
  };

  const createEnhancedExamHeader = (totalMarks) => {
    const headerDiv = document.createElement("div");
    headerDiv.className = "pdf-exam-header-enhanced";
    headerDiv.style.cssText = `
      text-align: center;
      padding: 8mm 0 6mm;
      border-bottom: 2pt solid #000;
      margin-bottom: 8mm;
      background: white;
    `;

    headerDiv.innerHTML = `
      <div style="font-size: 16pt; font-weight: bold; margin-bottom: 4mm; text-transform: uppercase; letter-spacing: 1pt; line-height: 1.2;">
        ${exam.metadata.heading}
      </div>
      <div style="font-size: 13pt; font-weight: 600; margin-bottom: 3mm; line-height: 1.2;">
        ${exam.metadata.subheading}
      </div>
      <div style="font-size: 12pt; font-weight: 600; margin-bottom: 5mm; line-height: 1.2;">
        ${exam.metadata.subjectName}
      </div>
      <div style="display: flex; justify-content: space-between; font-size: 11pt; font-weight: 600; 
                  border-top: 1.5pt solid #000; border-bottom: 1.5pt solid #000; padding: 3mm 0; line-height: 1.2;">
        <span>Time Allowed: ${exam.metadata.timeAllowed}</span>
        <span>Maximum Marks: ${totalMarks}</span>
      </div>
      <div style="text-align: left; font-size: 10pt; margin-top: 4mm; line-height: 1.3;">
        <strong>Instructions:</strong> ${exam.metadata.instructions}
      </div>
    `;

    return headerDiv;
  };
  const createEnhancedPageHeader = (pageNumber, totalPages) => {
    const headerDiv = document.createElement("div");
    headerDiv.style.cssText = `
      text-align: right;
      font-size: 9pt;
      color: #666;
      margin-bottom: 6mm;
      font-family: 'Times New Roman', serif;
    `;
    headerDiv.textContent = `Page ${pageNumber} of ${totalPages}`;
    return headerDiv;
  };

  // Enhanced section content with preserved math
  const createEnhancedSectionContent = async (section, sectionIndex) => {
    const sectionDiv = document.createElement("div");
    sectionDiv.className = "pdf-section-enhanced";
    sectionDiv.style.cssText = `
      margin-bottom: 6mm;
      page-break-inside: avoid;
    `;

    // Section title
    const titleDiv = document.createElement("div");
    titleDiv.style.cssText = `
      font-size: 13pt;
      font-weight: bold;
      margin-bottom: 3mm;
      padding-bottom: 2mm;
      border-bottom: 1pt solid #ccc;
      text-align: ${section.titleAlignment || "left"};
      line-height: 1.2;
    `;
    titleDiv.textContent =
      section.title + (section.isContinuation ? " (continued)" : "");
    sectionDiv.appendChild(titleDiv);

    // Questions with proper math rendering
    for (let i = 0; i < section.questions.length; i++) {
      const question = section.questions[i];
      const questionDiv = await createEnhancedQuestionContent(
        question,
        question.globalIndex + 1
      );
      sectionDiv.appendChild(questionDiv);
    }

    return sectionDiv;
  };

  // Enhanced question content with math preservation
  const createEnhancedQuestionContent = async (question, questionNumber) => {
    const questionDiv = document.createElement("div");
    questionDiv.className = "pdf-question-enhanced";
    questionDiv.style.cssText = `
      display: flex;
      margin: 3mm 0;
      page-break-inside: avoid;
      align-items: flex-start;
    `;

    // Question number
    const numberDiv = document.createElement("div");
    numberDiv.style.cssText = `
      font-weight: bold;
      margin-right: 4mm;
      min-width: 10mm;
      font-size: 11pt;
      line-height: 1.4;
      flex-shrink: 0;
    `;
    numberDiv.textContent = `${questionNumber}.`;
    questionDiv.appendChild(numberDiv);

    // Question content
    const contentDiv = document.createElement("div");
    contentDiv.style.cssText = `
      flex: 1;
      font-size: 11pt;
      line-height: 1.4;
    `;

    // Question text with preserved math
    const questionTextDiv = document.createElement("div");
    questionTextDiv.style.cssText = `margin-bottom: 3mm; line-height: 1.4;`;
    questionTextDiv.innerHTML = await processContentForHighQualityPDF(
      question.richContent
    );
    contentDiv.appendChild(questionTextDiv);

    // Options if present
    if (question.type === "with-options" && question.options?.length > 0) {
      const optionsDiv = document.createElement("div");
      optionsDiv.style.cssText = `margin: 3mm 0 3mm 6mm;`;

      for (let i = 0; i < question.options.length; i++) {
        const option = question.options[i];
        const optionDiv = document.createElement("div");
        optionDiv.style.cssText = `
          display: flex;
          margin: 2mm 0;
          align-items: flex-start;
          line-height: 1.4;
        `;

        const labelSpan = document.createElement("span");
        labelSpan.style.cssText = `
          font-weight: 600;
          margin-right: 4mm;
          min-width: 8mm;
          flex-shrink: 0;
        `;
        labelSpan.textContent = `(${String.fromCharCode(97 + i)})`;

        const textSpan = document.createElement("span");
        textSpan.style.cssText = `flex: 1;`;
        textSpan.innerHTML = await processContentForHighQualityPDF(
          option.richContent
        );

        optionDiv.appendChild(labelSpan);
        optionDiv.appendChild(textSpan);
        optionsDiv.appendChild(optionDiv);
      }

      contentDiv.appendChild(optionsDiv);
    }

    // Marks
    const marksDiv = document.createElement("div");
    marksDiv.style.cssText = `
      text-align: right;
      font-size: 9pt;
      color: #666;
      font-style: italic;
      margin-top: 3mm;
      line-height: 1.2;
    `;
    marksDiv.textContent = `[${question.marks || 1} mark${
      (question.marks || 1) !== 1 ? "s" : ""
    }]`;
    contentDiv.appendChild(marksDiv);

    questionDiv.appendChild(contentDiv);
    return questionDiv;
  };

  // Process content preserving math equations for high-quality PDF
  const processContentForHighQualityPDF = async (richContent) => {
    if (!richContent) return "";

    let html = richContent.html || "";

    // Preserve math equations by keeping math-field elements
    if (richContent.equations) {
      Object.values(richContent.equations).forEach((equation) => {
        const placeholderPattern = new RegExp(
          `<span[^>]*data-equation-id="${equation.id}"[^>]*>\\[EQUATION:${equation.id}\\]</span>`,
          "g"
        );
        const simplePlaceholderPattern = new RegExp(
          `\\[EQUATION:${equation.id}\\]`,
          "g"
        );

        // Create a proper math-field element for PDF
        const mathFieldHtml = `<math-field read-only style="display: inline; font-size: 11pt; font-family: 'Times New Roman', serif; background: transparent; border: none; padding: 0 2px; vertical-align: middle;">${equation.latex}</math-field>`;

        html = html.replace(placeholderPattern, mathFieldHtml);
        html = html.replace(simplePlaceholderPattern, mathFieldHtml);
      });
    }

    // Clean up edit mode artifacts but preserve math-fields
    html = html.replace(
      /<button[^>]*class="math-delete-btn"[^>]*>.*?<\/button>/g,
      ""
    );
    html = html.replace(/\s*edit-mode/g, "");
    html = html.replace(/data-equation-id="[^"]*"/g, "");

    return html;
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);

    try {
      // Exit edit modes and clear selections
      if (isEditMode) {
        setEditingMode(false);
      }
      clearSelection();

      // Wait for UI updates
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Get organized content using existing logic
      const organizedContent = getEnhancedContentForPDF();

      // Convert math equations to images
      console.log("Converting math equations to images...");
      const mathImages = await convertMathEquationsToImages(exam);
      console.log("Math conversion complete");

      // Generate filename
      const timestamp = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[:-]/g, "");
      const filename = `${
        exam.metadata.subjectName || "Question_Paper"
      }_${timestamp}.pdf`;

      // Create PDF document
      const MyDocument = () => (
        <PDFDocument
          examData={exam}
          organizedContent={organizedContent}
          mathImages={mathImages}
        />
      );

      // Generate PDF
      console.log("Generating PDF with @react-pdf/renderer...");
      const asPdf = pdf(<MyDocument />);
      const blob = await asPdf.toBlob();

      // Download the PDF
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showMessage("High-quality PDF generated successfully!");
    } catch (error) {
      console.error("PDF generation failed:", error);
      showMessage("PDF generation failed. Trying fallback method...");

      // Fallback to existing print method
      await setupEnhancedPrintFallback();
      setTimeout(() => window.print(), 300);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const previewPDF = async () => {
    try {
      const organizedContent = getEnhancedContentForPDF();
      const mathImages = await convertMathEquationsToImages(exam);

      const MyDocument = () => (
        <PDFDocument
          examData={exam}
          organizedContent={organizedContent}
          mathImages={mathImages}
        />
      );

      const asPdf = pdf(<MyDocument />);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);

      // Open in new tab for preview
      window.open(url, "_blank");
    } catch (error) {
      console.error("PDF preview failed:", error);
      showMessage("PDF preview failed.");
    }
  };
  const setupEnhancedPrintFallback = async () => {
    const existingStyles = document.querySelector("#enhanced-print-styles");
    if (existingStyles) {
      existingStyles.remove();
    }

    const printStyles = document.createElement("style");
    printStyles.id = "enhanced-print-styles";
    printStyles.innerHTML = `
      @page {
        margin: 8mm;
        size: ${exam.pagination.paperSize.toLowerCase()};
      }
      
      @media print {
        .tree-sidebar, .top-toolbar { 
          display: none !important; 
        }
        
        .canvas-container { 
          padding: 0 !important; 
          background: white !important; 
          margin: 0 !important;
        }
        
        .canvas { 
          margin: 0 !important; 
          padding: 0 !important; 
          box-shadow: none !important; 
          border: none !important;
          min-width: auto !important;
          background: white !important;
        }
        
        .page { 
          page-break-after: always; 
          padding: 8mm !important; 
          margin: 0 !important;
          border: none !important;
          box-shadow: none !important;
          min-height: auto !important;
          background: white !important;
        }
        
        .page:last-child { 
          page-break-after: avoid; 
        }
        
        .exam-header {
          padding: 6mm 0 4mm !important;
          margin-bottom: 6mm !important;
        }
        
        .section {
          margin: 3mm 0 !important;
          padding: 3mm !important;
          page-break-inside: avoid;
        }
        
        .question-item {
          margin: 2mm 0 !important;
          padding: 2mm !important;
          page-break-inside: avoid;
        }
        
        .edit-icon, .selection-indicator, .math-delete-btn,
        .node-actions, .inline-editable:hover .edit-icon { 
          display: none !important; 
        }
        
        .math-inline {
          border: none !important; 
          background: transparent !important; 
          padding: 0 2px !important;
          margin: 0 1px !important;
          display: inline !important;
        }
        
        .math-inline.edit-mode { 
          border: none !important; 
          background: none !important; 
          padding: 0 2px !important; 
        }
        
        math-field {
          display: inline !important;
          border: none !important;
          background: transparent !important;
          font-size: 11pt !important;
          font-family: 'Times New Roman', serif !important;
          padding: 0 !important;
          margin: 0 1px !important;
          vertical-align: middle !important;
        }
      }
    `;

    document.head.appendChild(printStyles);

    // Wait for math fields to render properly
    await new Promise((resolve) => setTimeout(resolve, 500));
  };

  const calculateTotalMarks = () => {
    return exam.pages.reduce(
      (total, page) =>
        total +
        page.sections.reduce(
          (sectionTotal, section) =>
            sectionTotal +
            section.questions.reduce(
              (questionTotal, question) =>
                questionTotal + (question.marks || 1),
              0
            ),
          0
        ),
      0
    );
  };

  const totalPages = calculateTotalPages();
  const totalMarks = calculateTotalMarks();
  const isEditMode = isEditingQuestion; // Disable PDF when editing

  return (
    <>
      <div className={`top-toolbar ${isEditMode ? "edit-mode" : ""}`}>
        <div className="toolbar-container">
          <div className="toolbar-section">
            <div className="logo-section">
              <FileText size={18} className="logo-icon" />
              <div className="logo-text">
                <span className="app-name">Question Paper Builder</span>
                <span className="app-subtitle">{exam.examType}</span>
              </div>
            </div>

            <div className="toolbar-actions">
              <div className="action-group">
                <button
                  onClick={addSection}
                  className="toolbar-btn primary"
                  title="Add a new section to the question paper"
                  disabled={isEditMode}
                >
                  <Plus size={16} />
                  <span>Add Section</span>
                </button>
                <button
                  onClick={addQuestionToSection}
                  className={`toolbar-btn primary ${
                    isSectionSelected ? "highlighted" : ""
                  }`}
                  title={
                    !hasAnySections
                      ? "Add a section first before adding questions"
                      : isSectionSelected
                      ? "Add question to selected section"
                      : "Add question to the last section"
                  }
                  disabled={isEditMode || !hasAnySections}
                >
                  <Plus size={16} />
                  <span>Add Question</span>
                  {isSectionSelected && !isEditMode && (
                    <span className="selection-dot"></span>
                  )}
                </button>
              </div>

              <div className="divider"></div>

              <div className="action-group">
                <button
                  onClick={() => setShowPageSettings(true)}
                  className="toolbar-btn secondary"
                  title="Configure page settings and paper size"
                  disabled={isEditMode}
                >
                  <Settings size={16} />
                  <span>Settings</span>
                </button>
                <button
                  onClick={generatePDF}
                  className="toolbar-btn success"
                  disabled={isGeneratingPDF || isEditMode}
                  title={
                    isEditMode
                      ? "Finish editing to download PDF"
                      : "Download PDF version of the question paper"
                  }
                >
                  {isGeneratingPDF ? (
                    <>
                      <div className="spinner"></div>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      <span>Download PDF</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {showSuccessMessage && (
          <div className="success-message">
            <Check size={16} />
            <span>{showSuccessMessage}</span>
          </div>
        )}

        {isEditMode && (
          <div className="edit-mode-indicator">
            <div className="edit-indicator-content">
              <div className="edit-indicator-icon">
                <Type size={16} />
              </div>
              <span>Question Edit Mode</span>
              <div className="edit-indicator-pulse"></div>
            </div>
          </div>
        )}
      </div>

      <PageSettingsModal
        isOpen={showPageSettings}
        onClose={() => setShowPageSettings(false)}
        exam={exam}
        onUpdate={updateExam}
      />

      <style jsx>{`
        .top-toolbar {
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
          padding: 0.5rem 1rem; /* ✅ Reduced padding for smaller height */
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          z-index: 100;
          position: relative;
          transition: all 0.3s ease;
          min-height: 60px; /* ✅ Reduced minimum height */
        }

        .top-toolbar.edit-mode {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border-bottom-color: #94a3b8;
          box-shadow: 0 2px 8px rgba(148, 163, 184, 0.15);
        }

        .toolbar-container {
          max-width: 2000px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1.5rem; /* ✅ Reduced gap */
          padding: 0 0.5rem; /* ✅ Reduced padding */
          min-height: 48px; /* ✅ Reduced minimum height */
        }

        .toolbar-section {
          display: flex;
          align-items: center;
          gap: 1rem; /* ✅ Reduced gap */
        }

        .logo-section {
          display: flex;
          align-items: center;
          gap: 0.5rem; /* ✅ Reduced gap */
        }

        .logo-icon {
          color: #3b82f6;
          flex-shrink: 0; /* ✅ Prevent icon shrinking */
        }

        .app-name {
          font-size: 0.9rem; /* ✅ Slightly smaller font */
          font-weight: 600;
          color: #1e293b;
          letter-spacing: -0.025em;
          white-space: nowrap; /* ✅ Prevent text wrapping */
        }

        .toolbar-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem; /* ✅ Reduced gap */
        }

        .action-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .divider {
          width: 1px;
          height: 20px; /* ✅ Reduced height */
          background: #e2e8f0;
          flex-shrink: 0; /* ✅ Prevent shrinking */
        }

        .toolbar-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.875rem; /* ✅ Reduced padding */
          border: 1px solid transparent;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.8rem; /* ✅ Slightly smaller font */
          font-weight: 500;
          transition: all 0.2s ease;
          position: relative;
          font-family: inherit;
          white-space: nowrap;
          min-width: fit-content; /* ✅ Prevent shrinking below content */
          flex-shrink: 0; /* ✅ Prevent shrinking */
        }

        .toolbar-btn:disabled {
          opacity: 0.4; /* ✅ More obvious disabled state */
          cursor: not-allowed;
          transform: none;
          background: #f1f5f9 !important;
          color: #94a3b8 !important;
          border-color: #e2e8f0 !important;
        }

        .toolbar-btn:disabled:hover {
          transform: none;
          box-shadow: none;
        }

        .toolbar-btn.primary {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .toolbar-btn.primary:hover:not(:disabled) {
          background: #2563eb;
          border-color: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .toolbar-btn.primary.highlighted:not(:disabled) {
          background: #1e40af;
          border-color: #1e40af;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .toolbar-btn.secondary {
          background: #ffffff;
          color: #374151;
          border-color: #d1d5db;
        }
        .logo-section {
          display: flex;
          align-items: center;
          gap: 0.5rem; /* ✅ Reduced gap */
        }

        .logo-text {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
        }

        .app-subtitle {
          font-size: 0.7rem;
          font-weight: 600;
          color: #64748b;
          line-height: 1;
          white-space: nowrap;
        }

        /* Update responsive design for mobile */
        @media (max-width: 768px) {
          .app-name,
          .app-subtitle {
            display: none;
          }
        }

        .toolbar-btn.secondary:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #9ca3af;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .toolbar-btn.success {
          background: #10b981;
          color: white;
          border-color: #10b981;
        }

        .toolbar-btn.success:hover:not(:disabled) {
          background: #059669;
          border-color: #059669;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
        }

        .toolbar-btn span {
          flex-shrink: 0; /* ✅ Prevent text shrinking */
        }

        .selection-dot {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 8px;
          height: 8px;
          background: #f59e0b;
          border: 2px solid white;
          border-radius: 50%;
          animation: pulse 2s infinite;
          flex-shrink: 0; /* ✅ Prevent shrinking */
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.2);
          }
        }

        .stats-panel {
          display: flex;
          align-items: center;
          gap: 0.75rem; /* ✅ Reduced gap */
          padding: 0.375rem 0.75rem; /* ✅ Reduced padding */
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          flex-shrink: 0; /* ✅ Prevent shrinking */
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.1rem; /* ✅ Reduced gap */
          min-width: fit-content; /* ✅ Prevent shrinking */
        }

        .stat-value {
          font-size: 0.8rem; /* ✅ Smaller font */
          font-weight: 700;
          color: #1e293b;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.7rem; /* ✅ Smaller font */
          color: #64748b;
          font-weight: 500;
          line-height: 1;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid #ffffff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          flex-shrink: 0; /* ✅ Prevent shrinking */
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .success-message {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: #dcfce7;
          color: #166534;
          padding: 0.625rem 0.875rem; /* ✅ Reduced padding */
          border-radius: 6px;
          border: 1px solid #bbf7d0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem; /* ✅ Smaller font */
          font-weight: 500;
          animation: slideDown 0.3s ease-out;
          z-index: 200;
          white-space: nowrap;
        }

        .edit-mode-indicator {
          position: absolute;
          top: 100%;
          right: 1rem;
          transform: translateY(-2px);
          background: #ffffff;
          border: 1px solid #94a3b8;
          border-radius: 6px;
          padding: 0.375rem 0.75rem; /* ✅ Reduced padding */
          box-shadow: 0 2px 8px rgba(148, 163, 184, 0.15);
          animation: slideDown 0.3s ease-out;
          z-index: 200;
        }

        .edit-indicator-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem; /* ✅ Smaller font */
          font-weight: 600;
          color: #475569;
          white-space: nowrap;
        }

        .edit-indicator-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
        }

        .edit-indicator-pulse {
          width: 6px;
          height: 6px;
          background: #94a3b8;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .toolbar-container {
            gap: 1rem;
            padding: 0 0.25rem;
          }

          .toolbar-section {
            gap: 0.75rem;
          }

          .toolbar-actions {
            gap: 0.5rem;
          }

          .stats-panel {
            gap: 0.5rem;
            padding: 0.25rem 0.5rem;
          }
        }

        @media (max-width: 1024px) {
          .toolbar-container {
            flex-direction: column;
            gap: 0.75rem;
            align-items: stretch;
            min-height: auto;
          }

          .toolbar-section {
            justify-content: center;
          }

          .logo-section {
            justify-content: center;
          }

          .stats-panel {
            justify-content: center;
          }

          .top-toolbar {
            padding: 0.75rem 1rem;
            min-height: auto;
          }
        }

        @media (max-width: 768px) {
          .top-toolbar {
            padding: 0.5rem;
          }

          .toolbar-actions {
            flex-wrap: wrap;
            justify-content: center;
          }

          .toolbar-btn span {
            display: none;
          }

          .toolbar-btn {
            padding: 0.5rem;
            min-width: 40px;
            justify-content: center;
          }

          .app-name {
            display: none;
          }

          .stats-panel {
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .stat-item {
            min-width: 50px;
          }

          .divider {
            height: 16px;
          }
        }

        @media (max-width: 480px) {
          .toolbar-container {
            padding: 0;
          }

          .toolbar-actions {
            gap: 0.25rem;
          }

          .action-group {
            gap: 0.25rem;
          }

          .toolbar-btn {
            padding: 0.375rem;
            min-width: 36px;
          }
        }
      `}</style>
    </>
  );
};

export default TopToolbar;